import { useMemo } from 'react';
import { Globe, Send } from 'lucide-react';

interface LinkPreviewCardProps {
  /** The URL to preview */
  url: string;
}

/** Parse a URL and extract display-friendly components */
function parseUrl(raw: string): {
  domain: string;
  path: string;
  isTelegram: boolean;
  displayTitle: string;
} | null {
  try {
    const url = new URL(raw);
    const domain = url.hostname.replace(/^www\./, '');
    const isTelegram = domain === 't.me' || domain === 'telegram.me' || domain === 'telegram.org';
    const path = url.pathname === '/' ? '' : url.pathname;

    // Build a human-readable title from the URL path
    let displayTitle = '';
    if (isTelegram && path.length > 1) {
      // e.g. /channelname → "Channel: channelname"
      displayTitle = path.slice(1);
    } else if (path.length > 1) {
      // e.g. /blog/my-post → "blog / my-post"
      displayTitle = decodeURIComponent(path.slice(1)).replace(/\//g, ' / ');
    }

    return { domain, path, isTelegram, displayTitle };
  } catch {
    return null;
  }
}

/**
 * Telegram-styled link preview card.
 *
 * Renders a card mimicking Telegram's inline link preview with a left
 * accent border, site name, page title, and URL.
 * Telegram links (t.me) get blue branding; other links get a green bar.
 */
export function LinkPreviewCard({ url }: LinkPreviewCardProps) {
  const parsed = useMemo(() => parseUrl(url), [url]);
  if (!parsed) return null;

  const { domain, isTelegram, displayTitle } = parsed;

  return (
    <div className={`tg-link-preview${isTelegram ? ' tg-link-preview--telegram' : ''}`}>
      <div className="tg-link-preview-bar" />
      <div className="tg-link-preview-body">
        <div className="tg-link-preview-header">
          {isTelegram ? (
            <Send className="tg-link-preview-icon tg-link-preview-icon--telegram" />
          ) : (
            <Globe className="tg-link-preview-icon" />
          )}
          <span
            className="tg-link-preview-site"
            style={{ color: isTelegram ? '#2AABEE' : '#168ACD' }}
          >
            {isTelegram ? 'Telegram' : domain}
          </span>
        </div>
        {displayTitle && (
          <div className="tg-link-preview-title">{displayTitle}</div>
        )}
        <div className="tg-link-preview-url">{url}</div>
      </div>
    </div>
  );
}
